import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../../lib/api-client';

import type { CreateMcpConnectorInput, McpConnectorWire, RotateCredentialsInput } from '../types';

/**
 * Sprint A4 S1 PR 3 — hooks de leitura de MCP connectors.
 *
 * - `useMcpConnectors`: list completa do tenant (staleTime 30s).
 * - `useMcpConnector(id)`: detail com enabled gate (only fetch quando id defined).
 * - `useInvalidateMcpConnectors`: util pra mutations invalidarem o cache list.
 *
 * Cache key canonical: `['admin', 'integrations', 'mcp-connectors', ...]`.
 */

export const MCP_CONNECTORS_LIST_KEY = ['admin', 'integrations', 'mcp-connectors'] as const;

export function mcpConnectorDetailKey(
  id: string | undefined,
): readonly [string, string, string, string | undefined] {
  return ['admin', 'integrations', 'mcp-connector', id] as const;
}

export function useMcpConnectors(): ReturnType<typeof useQuery<McpConnectorWire[]>> {
  return useQuery({
    queryKey: MCP_CONNECTORS_LIST_KEY,
    queryFn: () => api.listMcpConnectors(),
    staleTime: 30_000,
  });
}

export function useMcpConnector(
  id: string | undefined,
): ReturnType<typeof useQuery<McpConnectorWire>> {
  return useQuery({
    queryKey: mcpConnectorDetailKey(id),
    queryFn: () => {
      if (id === undefined) throw new Error('mcpConnector id obrigatório');
      return api.getMcpConnector(id);
    },
    enabled: id !== undefined,
  });
}

export function useInvalidateMcpConnectors(): () => Promise<void> {
  const qc = useQueryClient();
  return async () => {
    await qc.invalidateQueries({ queryKey: MCP_CONNECTORS_LIST_KEY });
  };
}

/**
 * Mutation hooks — cada um invalida o cache list via useInvalidateMcpConnectors.
 * Pattern espelho dos hooks de useAppointments / useUsers (Sprint Frontend Desktop).
 */

export function useCreateMcpConnector(): ReturnType<
  typeof useMutation<McpConnectorWire, Error, CreateMcpConnectorInput>
> {
  const invalidate = useInvalidateMcpConnectors();
  return useMutation({
    mutationFn: (input: CreateMcpConnectorInput) => api.createMcpConnector(input),
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useDeleteMcpConnector(): ReturnType<
  typeof useMutation<void, Error, { id: string; version: number }>
> {
  const invalidate = useInvalidateMcpConnectors();
  return useMutation({
    mutationFn: ({ id, version }) => api.deleteMcpConnector(id, version),
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useTestHandshake(): ReturnType<
  typeof useMutation<{ status: 'pending'; connectorId: string }, Error, string>
> {
  const invalidate = useInvalidateMcpConnectors();
  return useMutation({
    mutationFn: (id: string) => api.testMcpHandshake(id),
    onSuccess: () => {
      // Cache invalidation aciona refetch do card → status 'pending-handshake'.
      // WS event mcp.handshake-completed (Task 10) faz invalidation final.
      void invalidate();
    },
  });
}

export function useRotateCredentials(): ReturnType<
  typeof useMutation<
    McpConnectorWire,
    Error,
    { id: string; input: RotateCredentialsInput; version: number }
  >
> {
  const invalidate = useInvalidateMcpConnectors();
  return useMutation({
    mutationFn: ({ id, input, version }) => api.rotateMcpCredentials(id, input, version),
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function usePauseIntegrations(): ReturnType<
  typeof useMutation<{ paused: true; pausedAt: string }, Error, void>
> {
  const invalidate = useInvalidateMcpConnectors();
  return useMutation({
    mutationFn: () => api.pauseIntegrations(),
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useResumeIntegrations(): ReturnType<
  typeof useMutation<{ paused: false }, Error, void>
> {
  const invalidate = useInvalidateMcpConnectors();
  return useMutation({
    mutationFn: () => api.resumeIntegrations(),
    onSuccess: () => {
      void invalidate();
    },
  });
}
